import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  getGoogleAnalyticsIntegration,
  removeGoogleAnalyticsIntegration,
  updateGoogleAnalyticsIntegration,
  type GoogleAnalyticsOrganizationScope,
} from './google-analytics.api'
import type {
  GoogleAnalyticsIntegration,
  GoogleAnalyticsIntegrationInput,
} from './google-analytics.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type GoogleAnalyticsIntegrationQueryKey = [AdminQueryRoot & GoogleAnalyticsOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const googleAnalyticsQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'googleAnalytics' }, { organizationId }),
  ],
  integration: ({ organizationId }: OrganizationScope = {}): GoogleAnalyticsIntegrationQueryKey =>
    buildScopedAdminQueryKey(googleAnalyticsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'integration',
    }),
}

export const googleAnalyticsQueries = {
  integration: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: googleAnalyticsQueryKeys.integration({ organizationId }),
      queryFn: getGoogleAnalyticsIntegration,
    }),
}

type GoogleAnalyticsIntegrationParams = OrganizationScope &
  QueryParams<ReturnType<typeof googleAnalyticsQueries.integration>>

function syncIntegration(organizationId: number, integration: GoogleAnalyticsIntegration) {
  queryClient.setQueryData(googleAnalyticsQueryKeys.integration({ organizationId }), integration)
}

export function useGoogleAnalyticsIntegration({
  organizationId,
  ...params
}: GoogleAnalyticsIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...googleAnalyticsQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSuspenseGoogleAnalyticsIntegration({
  organizationId,
  ...params
}: GoogleAnalyticsIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...googleAnalyticsQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateGoogleAnalyticsIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: GoogleAnalyticsIntegrationInput) =>
      updateGoogleAnalyticsIntegration({ organizationId, ...values }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}

export function useRemoveGoogleAnalyticsIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => removeGoogleAnalyticsIntegration({ organizationId }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}
